import { FC } from "react"
import {
  CONFIG_ADMIN_INPUT_FULL_CLASS,
  ConfigAdminNumberInput,
  ConfigAdminPanelStackedField,
  ConfigAdminToggleRow
} from "../../../config-admin-form-primitives"
import { ConfigAdminPanel } from "../../../config-admin-panel"
import BBSwitch from "../../../../form/Switch"
import {
  RateCardsConfigDetailsData,
  RateCardsConfigTabOption
} from "../../../../../../types"

interface Props {
  details: RateCardsConfigDetailsData
  setDetails: (details: RateCardsConfigDetailsData) => void
  tab: string
}

interface TermLoanRowProps {
  loanKey: string
  defaultLabel: string
  labelValue: string
  enabled: boolean
  onLabelChange: (v: string) => void
  onEnabledChange: (v: boolean) => void
}

const TermLoanRow: FC<TermLoanRowProps> = ({
  loanKey,
  defaultLabel,
  labelValue,
  enabled,
  onLabelChange,
  onEnabledChange
}) => (
  <div className="px-4 py-3">
    <div className="flex items-center gap-4">
      <div className="min-w-0 flex-1">
        <input
          id={`rate-card-term-${loanKey}`}
          type="text"
          value={labelValue}
          onChange={e => onLabelChange(e.target.value)}
          disabled={!enabled}
          placeholder={defaultLabel}
          className={CONFIG_ADMIN_INPUT_FULL_CLASS}
          aria-label={defaultLabel}
        />
      </div>
      <div className="shrink-0 self-center">
        <BBSwitch enabled={enabled} setEnabled={onEnabledChange} />
      </div>
    </div>
    <p className="mt-1 font-mono text-xs text-gray-400">{loanKey}</p>
  </div>
)

const PURCHASE_LOAN_TYPES = [
  { key: "thirty_year", label: "30 Year Fixed" },
  { key: "fifteen_year", label: "15 Year Fixed" },
  { key: "arm", label: "ARM" },
  { key: "fha", label: "FHA" },
  { key: "va", label: "VA" },
  { key: "usda", label: "USDA" },
  { key: "thirty_year_jumbo", label: "30 Year Jumbo" },
  { key: "fifteen_year_jumbo", label: "15 Year Jumbo" },
  { key: "jumbo_arm", label: "Jumbo ARM" },
  { key: "thirty_year_ldp", label: "30 Year LDP" },
  { key: "fifteen_year_ldp", label: "15 Year LDP" },
  { key: "va_ldp", label: "VA LDP" },
  { key: "va_zdp", label: "VA ZDP" },
  { key: "fifteen_year_va", label: "15 Year VA" },
  { key: "fifteen_year_va_ldp", label: "15 Year VA LDP" },
  { key: "fifteen_year_va_zdp", label: "15 Year VA ZDP" }
]

const REFINANCE_LOAN_TYPES = [
  { key: "thirty_year_refi", label: "30 Year Refinance" },
  { key: "fifteen_year_refi", label: "15 Year Refinance" },
  { key: "arm_refi", label: "ARM Refinance" },
  { key: "fha_refi", label: "FHA Refinance" },
  { key: "va_refi", label: "VA Refinance" }
]

const toDefaultTabOptions = (loanTypes: { key: string; label: string }[]) =>
  loanTypes.map(loanType => ({
    type: loanType.key,
    label: loanType.label
  }))

const ManageRateCardsFormContent: FC<Props> = ({
  details,
  setDetails,
  tab
}) => {
  const updateField = <K extends keyof RateCardsConfigDetailsData>(
    field: K,
    value: RateCardsConfigDetailsData[K]
  ) => {
    setDetails({ ...details, [field]: value })
  }

  const purchaseTabs = details.tabs?.purchase ?? []
  const refinanceTabs = details.tabs?.refinance ?? []

  const purchaseLoanTypesEnabled = purchaseTabs.length > 0
  const refinanceLoanTypesEnabled = refinanceTabs.length > 0

  const getTabOption = (
    options: RateCardsConfigTabOption[],
    loanType: string
  ) => options.find(option => option.type === loanType)

  const setLoanTypeEnabled = (
    group: "purchase" | "refinance",
    loanType: string,
    defaultLabel: string,
    nextEnabled: boolean
  ) => {
    const currentOptions = [...details.tabs[group]]
    const index = currentOptions.findIndex(option => option.type === loanType)
    if (nextEnabled && index === -1) {
      currentOptions.push({ type: loanType, label: defaultLabel })
    } else if (!nextEnabled && index > -1) {
      currentOptions.splice(index, 1)
    }
    updateField("tabs", { ...details.tabs, [group]: currentOptions })
  }

  const setLoanTypeLabel = (
    group: "purchase" | "refinance",
    loanType: string,
    defaultLabel: string,
    nextLabel: string
  ) => {
    const currentOptions = [...details.tabs[group]]
    const index = currentOptions.findIndex(option => option.type === loanType)
    if (index === -1) {
      return
    }
    const label = nextLabel.trim() || defaultLabel

    currentOptions[index] = { ...currentOptions[index], label }

    updateField("tabs", { ...details.tabs, [group]: currentOptions })
  }

  if (tab === "general") {
    return (
      <div className="space-y-6 pt-2">
        <ConfigAdminPanel>
          <ConfigAdminPanelStackedField
            label="Max Results"
            fieldKey="max_results"
            hint="Sets the maximum number of mortgage rate results displayed to the user"
            htmlFor="max-results"
          >
            <ConfigAdminNumberInput
              id="max-results"
              value={details.max_results}
              onChange={v => updateField("max_results", v ?? 0)}
              parse="int"
              min={1}
              className={CONFIG_ADMIN_INPUT_FULL_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminToggleRow
            label="Disable CTA"
            fieldKey="disable_cta"
            description='Hides or disables the "Customize Rate" call-to-action button'
            enabled={details.disable_cta ?? false}
            onChange={(value: boolean) => updateField("disable_cta", value)}
            last={false}
          />

          <ConfigAdminToggleRow
            label="Disable Average"
            fieldKey="disable_avg"
            description='Hides the "National Average" above the product tabs'
            enabled={details.disable_avg ?? false}
            onChange={(value: boolean) => updateField("disable_avg", value)}
            last={false}
          />
        </ConfigAdminPanel>
      </div>
    )
  }

  if (tab === "terms") {
    return (
      <div className="space-y-6 pt-2">
        <ConfigAdminPanel>
          <ConfigAdminToggleRow
            label="Purchase"
            description="Control which purchase loan types appear on the rate cards"
            enabled={purchaseLoanTypesEnabled}
            onChange={(value: boolean) => {
              if (!value) {
                setDetails({
                  ...details,
                  tabs: {
                    ...details.tabs,
                    purchase: []
                  }
                })
                return
              }
              if (details.tabs.purchase.length === 0) {
                setDetails({
                  ...details,
                  tabs: {
                    ...details.tabs,
                    purchase: toDefaultTabOptions(PURCHASE_LOAN_TYPES)
                  }
                })
              }
            }}
            last={false}
          />
          {purchaseLoanTypesEnabled && (
            <div className="divide-y divide-gray-100 border-t border-gray-100">
              {PURCHASE_LOAN_TYPES.map(lt => (
                <TermLoanRow
                  key={lt.key}
                  loanKey={lt.key}
                  defaultLabel={lt.label}
                  labelValue={
                    getTabOption(purchaseTabs, lt.key)?.label ?? lt.label
                  }
                  enabled={Boolean(getTabOption(purchaseTabs, lt.key))}
                  onLabelChange={v =>
                    setLoanTypeLabel("purchase", lt.key, lt.label, v)
                  }
                  onEnabledChange={v =>
                    setLoanTypeEnabled("purchase", lt.key, lt.label, v)
                  }
                />
              ))}
            </div>
          )}
        </ConfigAdminPanel>

        <ConfigAdminPanel>
          <ConfigAdminToggleRow
            label="Refinance"
            description="Control which refinance loan types appear on the rate cards"
            enabled={refinanceLoanTypesEnabled}
            onChange={(value: boolean) => {
              if (!value) {
                setDetails({
                  ...details,
                  tabs: {
                    ...details.tabs,
                    refinance: []
                  }
                })
                return
              }
              if (details.tabs.refinance.length === 0) {
                setDetails({
                  ...details,
                  tabs: {
                    ...details.tabs,
                    refinance: toDefaultTabOptions(REFINANCE_LOAN_TYPES)
                  }
                })
              }
            }}
            last={false}
          />
          {refinanceLoanTypesEnabled && (
            <div className="divide-y divide-gray-100 border-t border-gray-100">
              {REFINANCE_LOAN_TYPES.map(lt => (
                <TermLoanRow
                  key={lt.key}
                  loanKey={lt.key}
                  defaultLabel={lt.label}
                  labelValue={
                    getTabOption(refinanceTabs, lt.key)?.label ?? lt.label
                  }
                  enabled={Boolean(getTabOption(refinanceTabs, lt.key))}
                  onLabelChange={v =>
                    setLoanTypeLabel("refinance", lt.key, lt.label, v)
                  }
                  onEnabledChange={v =>
                    setLoanTypeEnabled("refinance", lt.key, lt.label, v)
                  }
                />
              ))}
            </div>
          )}
        </ConfigAdminPanel>
      </div>
    )
  }

  return null
}

export default ManageRateCardsFormContent
